import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const fmtBytes = (b) => {
  if (b === null || b === undefined) return null;
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, v = b;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
};
export const fmtNum = (v, suffix = "") => (v === null || v === undefined ? null : `${v}${suffix}`);
export const fmtTime = (s) => (s ? new Date(s).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");
export const fmtDur = (sec) => {
  if (sec === null || sec === undefined) return null;
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  return d ? `${d}g ${h}s` : h ? `${h}s ${m}dk` : `${m}dk`;
};

export const TONE = { ok: "text-emerald-700 bg-emerald-50 border-emerald-200", warn: "text-amber-700 bg-amber-50 border-amber-200", bad: "text-red-700 bg-red-50 border-red-200", na: "text-slate-500 bg-slate-50 border-slate-200", info: "text-blue-700 bg-blue-50 border-blue-200" };
export const level = (v, warn, bad) => (v === null || v === undefined ? "na" : v >= bad ? "bad" : v >= warn ? "warn" : "ok");

export function Metric({ label, value, hint, tone = "ok", testId, unsupported = "Veri alınamıyor" }) {
  const na = value === null || value === undefined;
  return (
    <div data-testid={testId} className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] min-w-0">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold text-slate-500 truncate">{label}</div>
        <span className={`w-2 h-2 rounded-full ${na ? "bg-slate-300" : tone === "bad" ? "bg-red-500" : tone === "warn" ? "bg-amber-500" : "bg-emerald-500"}`} />
      </div>
      <div data-testid={testId && `${testId}-value`} className={`mt-2 font-bold tracking-tight ${na ? "text-sm text-slate-400" : "text-2xl text-slate-900"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
        {na ? unsupported : value}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-1 truncate">{hint}</div>}
    </div>
  );
}

export function Section({ title, right, children, testId, className = "" }) {
  return (
    <section data-testid={testId} className={`bg-white rounded-2xl border border-slate-200/80 p-5 min-w-0 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

export const usePoll = (url, ms, params) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const key = JSON.stringify(params || {});
  const ref = useRef(true);
  useEffect(() => {
    ref.current = true;
    const load = () => api.get(url, { params }).then((r) => { if (ref.current) { setData(r.data); setError(null); } })
      .catch((e) => ref.current && setError(e.response?.data?.detail || "Sunucuya ulaşılamadı"));
    load();
    const t = ms ? setInterval(() => { if (!document.hidden) load(); }, ms) : null;
    return () => { ref.current = false; t && clearInterval(t); };
  }, [url, ms, key]);
  return { data, error, setData };
};

const toLocal = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
export const PRESETS = [["1s", 1], ["6s", 6], ["24s", 24], ["7g", 168], ["30g", 720]];

export function RangePicker({ range, setRange, testId = "range" }) {
  const preset = (h) => { const e = new Date(); setRange({ start: toLocal(new Date(e - h * 3600e3)), end: toLocal(e) }); };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      {PRESETS.map(([l, h]) => (
        <button key={l} data-testid={`${testId}-preset-${l}`} onClick={() => preset(h)} className="px-2.5 py-1 rounded-lg border border-slate-200 text-xs font-medium hover:bg-emerald-50 hover:border-emerald-200">{l === "1s" ? "1 saat" : l === "6s" ? "6 saat" : l === "24s" ? "24 saat" : l === "7g" ? "7 gün" : "30 gün"}</button>
      ))}
      <input data-testid={`${testId}-start`} type="datetime-local" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-8 rounded-lg border border-slate-200 px-2 text-xs bg-white" />
      <span className="text-xs text-slate-400">–</span>
      <input data-testid={`${testId}-end`} type="datetime-local" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-8 rounded-lg border border-slate-200 px-2 text-xs bg-white" />
    </div>
  );
}

export const defaultRange = (h = 24) => { const e = new Date(); return { start: toLocal(new Date(e - h * 3600e3)), end: toLocal(e) }; };
export const rangeParams = (r) => ({ start: new Date(r.start).toISOString(), end: new Date(r.end).toISOString() });

export function ExportButton({ kind, range, params, testId }) {
  const run = async () => {
    try {
      const res = await api.get("/admin/monitor/export", { params: { kind, ...(params || (range ? rangeParams(range) : {})) }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `emlakplus-${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Dışa aktarma başarısız"); }
  };
  return <Button data-testid={testId || `export-${kind}`} size="sm" variant="outline" onClick={run}><Download className="w-4 h-4 mr-1.5" />CSV / Excel</Button>;
}

export function Pager({ page, size, total, setPage, testId }) {
  const pages = Math.max(1, Math.ceil(total / size));
  return (
    <div className="flex items-center justify-between mt-3 text-xs text-slate-500" data-testid={testId}>
      <span>{total} kayıt</span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testId}-prev`}>Önceki</Button>
        <span>{page} / {pages}</span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`${testId}-next`}>Sonraki</Button>
      </div>
    </div>
  );
}
